import { FiCheck } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/custom/loader';

export const MODALITY_LABEL = {
  multiple_choice: 'Múltipla escolha',
  discursive: 'Discursiva',
  essay: 'Redação',
  true_false: 'V/F',
  short_answer: 'Curta'
};

export const ETAPA_LABEL = {
  serie_3_em: '3ª EM',
  ano_9_ef: '9º EF',
  ano_5_ef: '5º EF',
  ano_2_ef: '2º EF'
};

export const VISIBILITY_LABEL = {
  train: 'Não exclusiva',
  open: 'Aberta',
  exam: 'Exclusiva para provas',
  mock: 'Exclusiva para simulados',
  exam_and_mock: 'Exclusiva para provas e simulados'
};

export function buildIncludedIndex(included = []) {
  const idx = {};
  included.forEach(item => {
    idx[`${item.type}:${item.id}`] = item;
  });
  return idx;
}

function Section({ title, delay = 0, children }) {
  return (
    <section
      className="opacity-0"
      style={{
        animation: `section-in 320ms cubic-bezier(0.23, 1, 0.32, 1) ${delay}ms forwards`
      }}
    >
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-light">{title}</h4>
      {children}
    </section>
  );
}

function TagRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <span className="min-w-[140px] text-xs text-neutral-light">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

// Injeta as keyframes uma única vez
if (typeof window !== 'undefined' && !document.getElementById('section-in-kf')) {
  const s = document.createElement('style');
  s.id = 'section-in-kf';
  s.textContent = `
    @keyframes section-in {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      @keyframes section-in { from { opacity: 0; } to { opacity: 1; } }
    }
  `;
  document.head.appendChild(s);
}

/**
 * Conteúdo do preview de questão (textos de apoio/comando/alternativas/classificação),
 * a partir de um recurso JSON:API cru. Os textos de apoio vêm inline no atributo
 * `motivator-texts` (N:N). Usado no Banco de Questões (dentro de um Sheet) e no
 * gerenciamento de provas/simulados/apostilas (dentro do Dialog centralizado).
 */
export function QuestionDetailContent({ question, loading = false }) {
  if (loading && !question) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loader />
      </div>
    );
  }

  if (!question) {
    return null;
  }

  const a = question.attributes || {};
  const taxs = a.taxonomies || [];
  const alts = a.alternatives || [];

  // Particiona taxonomies por tipo (heurística por nome/padrão)
  const SAEB_CODE = /^(D\d+|[259][A-Z]+\d+\.\d+|3[A-Z]+\d+\.\d+)/;
  const MATERIAS = new Set(['Matemática', 'Língua Portuguesa', 'Lingua Portuguesa']);
  const materia = taxs.find(t => MATERIAS.has(t.name))?.name;
  const descritorSaeb = taxs.find(t => SAEB_CODE.test(t.name))?.name;
  const subConteudo = taxs.filter(t => !MATERIAS.has(t.name) && !SAEB_CODE.test(t.name));

  const motivatorTexts = a['motivator-texts'] || [];
  const codigo = a['external-id'] || a.code || `#${question.id}`;

  return (
    <>
      {/* Cabeçalho fixo */}
      <div className="flex-shrink-0 border-b border-neutral-lightest bg-white px-6 py-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-sm tracking-wide text-foreground">{codigo}</span>
              <Badge
                variant="neutral-lightest"
                size="sm"
              >
                {MODALITY_LABEL[a.modality] || a.modality || '—'}
              </Badge>
              <Badge
                variant={a.published ? 'success-soft' : 'neutral-lightest'}
                size="sm"
              >
                {a.published ? 'Publicada' : 'Rascunho'}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-neutral">
            {a.etapa && <span>{ETAPA_LABEL[a.etapa] || a.etapa}</span>}
            {a.etapa && materia && <span className="text-neutral-light">·</span>}
            {materia && <span>{materia}</span>}
            {(a.etapa || materia) && descritorSaeb && <span className="text-neutral-light">·</span>}
            {descritorSaeb && <span className="font-mono">{descritorSaeb}</span>}
            {(a.etapa || materia || descritorSaeb) && a.visibility && <span className="text-neutral-light">·</span>}
            {a.visibility && <span>{VISIBILITY_LABEL[a.visibility] || a.visibility}</span>}
          </div>
        </div>
      </div>

      {/* Conteúdo rolável */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="space-y-6">
          {motivatorTexts.length > 0 && (
            <Section
              title="Texto motivador"
              delay={0}
            >
              {motivatorTexts.map(t => (
                <div
                  key={t.id}
                  className="prose prose-sm max-w-none leading-relaxed text-foreground"
                  dangerouslySetInnerHTML={{ __html: t.body }}
                />
              ))}
            </Section>
          )}

          {a.description && (
            <Section
              title="Enunciado da questão"
              delay={60}
            >
              <div
                className="prose prose-sm border-primary/60 bg-primary/[0.03] max-w-none border-l-2 py-2 pl-4 pr-2 text-sm leading-relaxed text-foreground"
                dangerouslySetInnerHTML={{ __html: a.description }}
              />
            </Section>
          )}

          {alts.length > 0 && (
            <Section
              title="Alternativas"
              delay={120}
            >
              <ol className="space-y-2">
                {alts.map((alt, i) => {
                  const letter = String.fromCharCode(65 + i); // A, B, C...
                  const correct = alt['is-correct'];
                  return (
                    <li
                      key={alt.id}
                      className={['flex items-start gap-3 rounded-lg border p-3 transition-colors duration-150', correct ? 'border-success/30 bg-success/[0.04] ring-success/15 ring-1' : 'border-neutral-lightest bg-white'].join(' ')}
                    >
                      <span className={['flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold', correct ? 'bg-success/15 text-success-dark' : 'bg-neutral-lightest text-neutral'].join(' ')}>{letter}</span>
                      <div className="flex-1 text-sm leading-relaxed">
                        <div dangerouslySetInnerHTML={{ __html: alt.description || '' }} />
                      </div>
                      {correct && (
                        <FiCheck
                          className="text-success-dark size-5 shrink-0"
                          strokeWidth={2.5}
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
            </Section>
          )}

          {(materia || a.etapa || descritorSaeb || subConteudo.length > 0) && (
            <Section
              title="Classificação"
              delay={180}
            >
              <div className="space-y-3">
                {materia && (
                  <TagRow label="Matéria">
                    <Badge
                      variant="default"
                      size="sm"
                    >
                      {materia}
                    </Badge>
                  </TagRow>
                )}
                {a.etapa && (
                  <TagRow label="Etapa">
                    <Badge
                      variant="neutral-lightest"
                      size="sm"
                    >
                      {ETAPA_LABEL[a.etapa] || a.etapa}
                    </Badge>
                  </TagRow>
                )}
                {descritorSaeb && (
                  <TagRow label="Descritor SAEB">
                    <Badge
                      variant="neutral-lightest"
                      size="sm"
                    >
                      <span className="font-mono text-xs">{descritorSaeb}</span>
                    </Badge>
                  </TagRow>
                )}
                {subConteudo.length > 0 && (
                  <TagRow label="Sub-conteúdos Ementa">
                    <div className="flex flex-wrap gap-1.5">
                      {subConteudo.map(t => (
                        <Badge
                          key={t.id}
                          variant="neutral-lightest"
                          size="sm"
                        >
                          {t.name}
                        </Badge>
                      ))}
                    </div>
                  </TagRow>
                )}
              </div>
            </Section>
          )}
        </div>
      </div>
    </>
  );
}
